import {
  useCallback,
  useEffect,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type Dispatch,
  type SetStateAction,
} from "react";
import { Pencil, Save, UploadCloud } from "lucide-react";
import { useAuth } from "../auth/useAuth";
import { bugMode, useBugMode } from "../lib/bugMode";
import { authEnabled } from "../auth/config";
import {
  fetchConnectContacts,
  getDocFields,
  overlayEnabled,
  putOverride,
} from "../lib/overlay";
import {
  applyConfig,
  BASELINE_CONFIG,
  deepMerge,
  getAppConfig,
  type AppConfig,
  type FooterTitleFormat,
  type ThemeMode,
} from "../lib/appConfig";

// Configuration Management modal. Two modes per the spec: Read (Edit / Close) and Edit (Save Draft /
// Publish / Cancel). The whole config object is stored JSON-encoded under one flat `config` field on
// two overlay records: config-draft (stage in-review, the WIP) and config-active (stage published,
// what the app resolves). C2b implements the Admin Profile section; App Settings (C3) fills the rest.
const CONFIG_DRAFT_ID = "config-draft";
const CONFIG_ACTIVE_ID = "config-active";

export type ConfigSection = "profile" | "settings";

const SECTION_META: Record<ConfigSection, { title: string; sub: string }> = {
  profile: { title: "Admin Profile", sub: "Name, contact, social links" },
  settings: { title: "General Settings", sub: "Title, appearance, header, Pages, footer" },
};

const MODE_OPTIONS: { label: string; value: ThemeMode }[] = [
  { label: "Day", value: "light" },
  { label: "Night", value: "dark" },
  { label: "System", value: "system" },
];

// Five on-brand accent options. `null` keeps the stylesheet default (petrol). The accent-soft/line/
// text tokens re-derive from --accent via color-mix, so one hex re-tints the whole app in both themes.
const THEME_COLORS: { label: string; value: string | null; hex: string }[] = [
  { label: "Petrol", value: null, hex: "#0d6e69" },
  { label: "Indigo", value: "#3b5b8c", hex: "#3b5b8c" },
  { label: "Forest", value: "#3f6f4e", hex: "#3f6f4e" },
  { label: "Plum", value: "#7d4a86", hex: "#7d4a86" },
  { label: "Wine", value: "#9e3b4e", hex: "#9e3b4e" },
];

// The config to edit: the live resolved config with any saved draft layered on top.
function configForEdit(): AppConfig {
  const live = getAppConfig();
  try {
    const raw = getDocFields(CONFIG_DRAFT_ID).config;
    if (raw) return deepMerge(live, JSON.parse(raw) as Partial<AppConfig>);
  } catch {
    // malformed draft — fall back to the live config
  }
  return live;
}

// Local auto-save (spec: "auto-save edits locally, sync with backend on Action click"). The overlay
// public read strips non-published fields, so a saved config-draft is not re-readable after reload;
// this localStorage WIP is the durable local copy that survives a reload until Publish clears it.
const WIP_KEY = "ir.config.wip.v1";

// Reactive "unsaved draft" flag — drives the indicator in the Admin menu and the modal.
function readHasWip(): boolean {
  try {
    return localStorage.getItem(WIP_KEY) != null;
  } catch {
    return false;
  }
}
let hasWip = readHasWip();
const wipListeners = new Set<() => void>();
function notifyWip(v: boolean) {
  if (v === hasWip) return;
  hasWip = v;
  for (const l of wipListeners) l();
}
/** True when an unpublished config draft is held locally. */
export function useConfigHasDraft(): boolean {
  return useSyncExternalStore(
    (cb) => {
      wipListeners.add(cb);
      return () => wipListeners.delete(cb);
    },
    () => hasWip,
    () => hasWip,
  );
}

function loadWip(): AppConfig | null {
  try {
    const raw = localStorage.getItem(WIP_KEY);
    return raw ? (JSON.parse(raw) as AppConfig) : null;
  } catch {
    return null;
  }
}
function saveWip(cfg: AppConfig) {
  try {
    localStorage.setItem(WIP_KEY, JSON.stringify(cfg));
    notifyWip(true);
  } catch {
    // storage unavailable — the backend save path still works
  }
}
function clearWip() {
  try {
    localStorage.removeItem(WIP_KEY);
  } catch {
    // ignore
  }
  notifyWip(false);
}

export function ConfigPage({ section }: { section: ConfigSection }) {
  // authEnabled is a build-time constant, so branching before any hook is safe.
  if (!authEnabled) {
    return <ConfigNotice title="Admin" body="Sign-in isn't configured in this build." />;
  }
  return <ConfigPageInner section={section} />;
}

function ConfigNotice({ title, body }: { title: string; body: string }) {
  return (
    <div className="wrap section">
      <p className="eyebrow">Admin</p>
      <h1 className="display" style={{ fontSize: 34, marginTop: 8 }}>
        {title}
      </h1>
      <p className="section-sub">{body}</p>
    </div>
  );
}

function ConfigPageInner({ section }: { section: ConfigSection }) {
  const { isAdmin, getApiToken } = useAuth();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<AppConfig>(getAppConfig);
  const [saving, setSaving] = useState<"draft" | "publish" | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState(false);
  const hasDraft = useConfigHasDraft();
  const [connectEmails, setConnectEmails] = useState<string[]>([]);
  const [connectPhones, setConnectPhones] = useState<string[]>([]);
  const [contactsState, setContactsState] = useState<"idle" | "loading" | "error">("idle");
  const loadContacts = useCallback(async () => {
    setContactsState("loading");
    const token = await getApiToken();
    const res = token ? await fetchConnectContacts(token) : null;
    if (res) {
      setConnectEmails(res.emails.map((e) => e.address));
      setConnectPhones(res.phones.map((ph) => ph.number));
      setContactsState("idle");
    } else {
      setContactsState("error");
    }
  }, [getApiToken]);

  // Auto-save the working draft locally while editing (survives reload until Publish).
  useEffect(() => {
    if (editing) saveWip(draft);
  }, [editing, draft]);

  if (!isAdmin) {
    return (
      <ConfigNotice
        title={SECTION_META[section].title}
        body="This page is for the site admin. Please sign in."
      />
    );
  }
  const meta = SECTION_META[section];
  const live = getAppConfig();

  const startEdit = () => {
    // Merge the WIP over the current edit base so drafts saved before a schema addition
    // (e.g. feedback.bugPickerPublic) inherit the new key instead of erasing it on publish.
    const wip = loadWip();
    setDraft(wip ? deepMerge(configForEdit(), wip as Partial<AppConfig>) : configForEdit());
    setErr(null);
    setJustSaved(false);
    setEditing(true);
    if (section === "profile") void loadContacts();
  };

  const setProfile = (patch: Partial<AppConfig["profile"]>) =>
    setDraft((d) => ({ ...d, profile: { ...d.profile, ...patch } }));
  const setSocial = (patch: Partial<AppConfig["profile"]["social"]>) =>
    setDraft((d) => ({ ...d, profile: { ...d.profile, social: { ...d.profile.social, ...patch } } }));

  const emailValid =
    !draft.profile.contactEmail || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.profile.contactEmail.trim());
  const canSave =
    section === "settings"
      ? draft.appTitle.trim() !== ""
      : Boolean(
          draft.profile.firstName.trim() &&
            draft.profile.lastName.trim() &&
            draft.profile.contactEmail.trim() &&
            emailValid,
        );

  const persist = async (mode: "draft" | "publish") => {
    if (!canSave) return;
    setSaving(mode);
    setErr(null);
    const token = await getApiToken();
    if (!token) {
      setErr("Your sign-in session expired — please sign in again.");
      setSaving(null);
      return;
    }
    const id = mode === "draft" ? CONFIG_DRAFT_ID : CONFIG_ACTIVE_ID;
    const stage = mode === "draft" ? "in-review" : "published";
    const ok = await putOverride(id, { stage, fields: { config: JSON.stringify(draft) } }, token);
    setSaving(null);
    if (!ok) {
      setErr("Save failed — your session may have expired. Sign out, sign back in, and retry.");
      return;
    }
    if (mode === "publish") {
      clearWip();
      applyConfig(draft); // reflect immediately in the running app
      setEditing(false);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2200);
    } else {
      setJustSaved(true); // draft synced to backend + local; stay in edit
      setTimeout(() => setJustSaved(false), 2200);
    }
  };

  return (
    <div className="wrap section cfg-page">
      <p className="eyebrow">Admin · {meta.sub}</p>
      <div className="cfg-page-head">
        <h1 className="display" style={{ fontSize: 34, marginTop: 8 }}>
          {meta.title}
        </h1>
        <span className="cfg-stage mono">
          {justSaved
            ? editing
              ? "Draft saved ✓"
              : "Published ✓"
            : editing
              ? "Editing draft"
              : "Active"}
        </span>
      </div>

      {!editing && hasDraft && (
        <div className="cfg-draft-hint">
          <span>You have an unpublished draft — click Edit to resume, or</span>
          <button type="button" className="cfg-link-btn" onClick={clearWip}>
            discard it
          </button>
          .
        </div>
      )}

      <div className="cfg-page-body">
        {editing ? (
          section === "settings" ? (
            <SettingsForm draft={draft} setDraft={setDraft} />
          ) : (
            <ProfileForm
              p={draft.profile}
              emailValid={emailValid}
              setProfile={setProfile}
              setSocial={setSocial}
              emails={connectEmails}
              phones={connectPhones}
              contactsState={contactsState}
              onRefreshContacts={() => void loadContacts()}
            />
          )
        ) : section === "settings" ? (
          <SettingsRead cfg={live} />
        ) : (
          <ProfileRead p={live.profile} />
        )}
        {err && <p className="cfg-err">{err}</p>}
        {editing && !overlayEnabled && (
          <p className="cfg-soon-note mono">Content API not configured — saving is disabled here.</p>
        )}
      </div>

      <div className="cfg-actions">
        {editing ? (
          <>
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => {
                setEditing(false);
                setErr(null);
              }}
              disabled={!!saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-sm"
              disabled={!overlayEnabled || !canSave || !!saving}
              onClick={() => persist("draft")}
            >
              <Save size={14} /> {saving === "draft" ? "Saving…" : "Save Draft"}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              disabled={!overlayEnabled || !canSave || !!saving}
              onClick={() => persist("publish")}
            >
              <UploadCloud size={14} /> {saving === "publish" ? "Publishing…" : "Publish"}
            </button>
          </>
        ) : (
          <button type="button" className="btn btn-sm btn-primary" onClick={startEdit}>
            <Pencil size={14} /> Edit
          </button>
        )}
      </div>
    </div>
  );
}

function ProfileRead({ p }: { p: AppConfig["profile"] }) {
  const rows: [string, string][] = [
    ["Name", `${p.firstName} ${p.lastName}`.trim() || "—"],
    ["Contact email", p.contactEmail || "—"],
    ["Contact phone", p.contactPhone || "—"],
    ["LinkedIn", p.social.linkedin || "—"],
    ["Facebook", p.social.facebook || "—"],
    ["Instagram", p.social.instagram || "—"],
  ];
  return (
    <div className="cfg-read">
      {rows.map(([k, v]) => (
        <div className="cfg-read-row" key={k}>
          <span className="cfg-read-k mono">{k}</span>
          <span className="cfg-read-v">{v}</span>
        </div>
      ))}
    </div>
  );
}

function ProfileForm({
  p,
  emailValid,
  setProfile,
  setSocial,
  emails,
  phones,
  contactsState,
  onRefreshContacts,
}: {
  p: AppConfig["profile"];
  emailValid: boolean;
  setProfile: (patch: Partial<AppConfig["profile"]>) => void;
  setSocial: (patch: Partial<AppConfig["profile"]["social"]>) => void;
  emails: string[];
  phones: string[];
  contactsState: "idle" | "loading" | "error";
  onRefreshContacts: () => void;
}) {
  const connectNote =
    contactsState === "loading"
      ? "Loading contacts from Amazon Connect…"
      : contactsState === "error"
        ? "Couldn't reach Amazon Connect — type the values manually."
        : `${emails.length} email${emails.length === 1 ? "" : "s"} · ${phones.length} number${
            phones.length === 1 ? "" : "s"
          } from Amazon Connect.`;
  return (
    <div className="cfg-form">
      <div className="cfg-row2">
        <label className="cfg-field">
          <span>First name *</span>
          <input value={p.firstName} maxLength={60} onChange={(e) => setProfile({ firstName: e.target.value })} />
        </label>
        <label className="cfg-field">
          <span>Last name *</span>
          <input value={p.lastName} maxLength={60} onChange={(e) => setProfile({ lastName: e.target.value })} />
        </label>
      </div>
      <p className="cfg-soon-note mono">
        {connectNote}{" "}
        <button
          type="button"
          className="cfg-link-btn"
          onClick={onRefreshContacts}
          disabled={contactsState === "loading"}
        >
          Refresh
        </button>
      </p>
      <label className="cfg-field">
        <span>Contact email *</span>
        <input
          type="email"
          list="cfg-connect-emails"
          value={p.contactEmail}
          maxLength={120}
          aria-invalid={!emailValid}
          placeholder={emails.length ? "Pick a Connect address or type one" : "name@example.com"}
          onChange={(e) => setProfile({ contactEmail: e.target.value })}
        />
        <datalist id="cfg-connect-emails">
          {emails.map((a) => (
            <option key={a} value={a} />
          ))}
        </datalist>
        {!emailValid && <span className="cfg-field-err">Enter a valid email address.</span>}
      </label>
      <label className="cfg-field">
        <span>
          Contact phone <span className="cfg-opt">optional</span>
        </span>
        <input
          type="tel"
          list="cfg-connect-phones"
          value={p.contactPhone}
          maxLength={40}
          placeholder={phones.length ? "Pick a Connect number or type one" : "+1 555 123 4567"}
          onChange={(e) => setProfile({ contactPhone: e.target.value })}
        />
        <datalist id="cfg-connect-phones">
          {phones.map((n) => (
            <option key={n} value={n} />
          ))}
        </datalist>
      </label>
      <label className="cfg-field">
        <span>
          LinkedIn <span className="cfg-opt">optional</span>
        </span>
        <input type="url" value={p.social.linkedin} maxLength={200} onChange={(e) => setSocial({ linkedin: e.target.value })} />
      </label>
      <div className="cfg-row2">
        <label className="cfg-field">
          <span>
            Facebook <span className="cfg-opt">optional</span>
          </span>
          <input type="url" value={p.social.facebook} maxLength={200} onChange={(e) => setSocial({ facebook: e.target.value })} />
        </label>
        <label className="cfg-field">
          <span>
            Instagram <span className="cfg-opt">optional</span>
          </span>
          <input type="url" value={p.social.instagram} maxLength={200} onChange={(e) => setSocial({ instagram: e.target.value })} />
        </label>
      </div>
    </div>
  );
}

function AppearanceForm({
  appearance,
  setAppearance,
}: {
  appearance: AppConfig["appearance"];
  setAppearance: (patch: Partial<AppConfig["appearance"]>) => void;
}) {
  return (
    <div className="cfg-form">
      <div className="cfg-field">
        <span>Default mode</span>
        <div className="cfg-seg" role="radiogroup" aria-label="Default mode">
          {MODE_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={appearance.mode === o.value}
              className={appearance.mode === o.value ? "is-active" : ""}
              onClick={() => setAppearance({ mode: o.value })}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>
      <div className="cfg-field">
        <span>Theme color</span>
        <div className="cfg-swatches" role="radiogroup" aria-label="Theme color">
          {THEME_COLORS.map((c) => (
            <button
              key={c.label}
              type="button"
              role="radio"
              aria-checked={(appearance.themeColor ?? null) === c.value}
              className={"cfg-swatch" + ((appearance.themeColor ?? null) === c.value ? " is-active" : "")}
              style={{ "--sw": c.hex } as CSSProperties}
              title={c.label}
              onClick={() => setAppearance({ themeColor: c.value })}
            >
              <span className="cfg-swatch-dot" />
              <span className="cfg-swatch-label">{c.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function AppearanceRead({ appearance }: { appearance: AppConfig["appearance"] }) {
  const modeLabel =
    appearance.mode === "light" ? "Day" : appearance.mode === "dark" ? "Night" : "System";
  const colorLabel =
    THEME_COLORS.find((c) => c.value === (appearance.themeColor ?? null))?.label ??
    appearance.themeColor ??
    "Petrol";
  return (
    <div className="cfg-read">
      <div className="cfg-read-row">
        <span className="cfg-read-k mono">Default mode</span>
        <span className="cfg-read-v">{modeLabel}</span>
      </div>
      <div className="cfg-read-row">
        <span className="cfg-read-k mono">Theme color</span>
        <span className="cfg-read-v">{colorLabel}</span>
      </div>
    </div>
  );
}

const TITLE_FORMATS: { value: FooterTitleFormat; label: string }[] = [
  { value: "name-app", label: "Name — App Title" },
  { value: "name-possessive-app", label: "Name's App Title" },
  { value: "app", label: "App Title only" },
];

const FOOTER_ITEMS: { key: keyof AppConfig["footer"]["show"]; label: string }[] = [
  { key: "aiReadable", label: "AI-readable résumé" },
  { key: "linkedin", label: "LinkedIn" },
  { key: "facebook", label: "Facebook" },
  { key: "instagram", label: "Instagram" },
  { key: "contactEmail", label: "Contact email" },
  { key: "contactPhone", label: "Contact phone" },
];

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="cfg-toggle">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="cfg-toggle-track" aria-hidden="true">
        <span className="cfg-toggle-thumb" />
      </span>
      <span className="cfg-toggle-label">{label}</span>
    </label>
  );
}

function SettingsForm({ draft, setDraft }: { draft: AppConfig; setDraft: Dispatch<SetStateAction<AppConfig>> }) {
  const setApp = (patch: Partial<AppConfig>) => setDraft((d) => ({ ...d, ...patch }));
  const setAppearance = (patch: Partial<AppConfig["appearance"]>) =>
    setDraft((d) => ({ ...d, appearance: { ...d.appearance, ...patch } }));
  const setHeader = (patch: Partial<AppConfig["header"]>) =>
    setDraft((d) => ({ ...d, header: { ...d.header, ...patch } }));
  const setFeedback = (patch: Partial<AppConfig["feedback"]>) =>
    setDraft((d) => ({ ...d, feedback: { ...(d.feedback ?? BASELINE_CONFIG.feedback), ...patch } }));
  const setFooter = (patch: Partial<AppConfig["footer"]>) =>
    setDraft((d) => ({ ...d, footer: { ...d.footer, ...patch } }));
  const setShow = (patch: Partial<AppConfig["footer"]["show"]>) =>
    setDraft((d) => ({ ...d, footer: { ...d.footer, show: { ...d.footer.show, ...patch } } }));
  const askAi = draft.askAi ?? BASELINE_CONFIG.askAi;
  const setAskAi = (patch: Partial<AppConfig["askAi"]>) =>
    setDraft((d) => ({ ...d, askAi: { ...BASELINE_CONFIG.askAi, ...d.askAi, ...patch } }));
  const setAskCopy = (patch: Partial<AppConfig["askAi"]["copy"]>) =>
    setDraft((d) => ({
      ...d,
      askAi: { ...BASELINE_CONFIG.askAi, ...d.askAi, copy: { ...BASELINE_CONFIG.askAi.copy, ...d.askAi?.copy, ...patch } },
    }));

  const f = draft.footer;
  const thisYear = new Date().getFullYear();
  const years: number[] = [];
  for (let y = thisYear; y >= 2018; y--) years.push(y);

  return (
    <div className="cfg-form">
      <p className="cfg-section-label mono">Site mode</p>
      <div className="cfg-toggles">
        <Toggle
          label="Interactive site"
          checked={draft.interactive}
          onChange={(v) => setApp({ interactive: v })}
        />
      </div>
      <p className="cfg-soon-note mono">
        Off = lights-out: visitors get only the flat Print/PDF résumé plus a bring-your-own-AI
        prompt — no Ask AI, Views, lenses, drill-downs, or feedback, and zero API/AI cost. Takes full
        effect on the next deploy (the flag bakes into the static config).
      </p>

      <label className="cfg-field">
        <span>App title *</span>
        <input value={draft.appTitle} maxLength={60} onChange={(e) => setApp({ appTitle: e.target.value })} />
      </label>

      <p className="cfg-section-label mono">Appearance</p>
      <AppearanceForm appearance={draft.appearance} setAppearance={setAppearance} />

      <p className="cfg-section-label mono">Header</p>
      <div className="cfg-toggles">
        <Toggle label="Case Study" checked={draft.header.caseStudy} onChange={(v) => setHeader({ caseStudy: v })} />
        <Toggle label="Design System" checked={draft.header.designSystem} onChange={(v) => setHeader({ designSystem: v })} />
      </div>

      <p className="cfg-section-label mono">Feedback</p>
      <div className="cfg-toggles">
        <Toggle
          label="Public bug picker"
          checked={draft.feedback?.bugPickerPublic ?? false}
          onChange={(v) => setFeedback({ bugPickerPublic: v })}
        />
      </div>
      <p className="cfg-soon-note mono">
        Point-and-click bug reports are always available to the signed-in admin; this opens the
        element picker to every visitor.
      </p>

      <p className="cfg-section-label mono">Pages</p>
      <p className="cfg-soon-note mono">Placeholder stub — per-page visibility settings come later.</p>

      <p className="cfg-section-label mono">Footer</p>
      <div className="cfg-row2">
        <label className="cfg-field">
          <span>Copyright year</span>
          <select
            value={f.copyrightYear === "auto" ? "auto" : String(f.copyrightYear)}
            onChange={(e) =>
              setFooter({ copyrightYear: e.target.value === "auto" ? "auto" : Number(e.target.value) })
            }
          >
            <option value="auto">Auto (current year)</option>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
        <label className="cfg-field">
          <span>Title format</span>
          <select value={f.titleFormat} onChange={(e) => setFooter({ titleFormat: e.target.value as FooterTitleFormat })}>
            {TITLE_FORMATS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="cfg-field">
        <span>Disclaimer text</span>
        <textarea
          rows={4}
          value={f.disclaimerText}
          maxLength={600}
          onChange={(e) => setFooter({ disclaimerText: e.target.value })}
        />
      </label>
      <div className="cfg-field">
        <span>Footer items</span>
        <div className="cfg-toggles">
          {FOOTER_ITEMS.map((it) => (
            <Toggle
              key={it.key}
              label={it.label}
              checked={f.show[it.key]}
              onChange={(v) => setShow({ [it.key]: v } as Partial<AppConfig["footer"]["show"]>)}
            />
          ))}
        </div>
      </div>

      <p className="cfg-section-label mono">Ask AI</p>
      <div className="cfg-row2">
        <label className="cfg-field">
          <span>Free questions (anonymous)</span>
          <input
            type="number"
            min={1}
            max={20}
            value={askAi.freeLimit}
            onChange={(e) => setAskAi({ freeLimit: Math.min(20, Math.max(1, Number(e.target.value) || 1)) })}
          />
        </label>
        <label className="cfg-field">
          <span>Bonus per verified email</span>
          <input
            type="number"
            min={1}
            max={20}
            value={askAi.emailGrant}
            onChange={(e) => setAskAi({ emailGrant: Math.min(20, Math.max(1, Number(e.target.value) || 1)) })}
          />
        </label>
      </div>
      <p className="cfg-soon-note mono">
        Enforced server-side (the breaker + per-IP cap are the cost backstops). The copy below shows in
        the out-of-questions panel.
      </p>
      <label className="cfg-field">
        <span>Out-of-questions heading</span>
        <input
          value={askAi.copy.exhaustedTitle}
          maxLength={80}
          onChange={(e) => setAskCopy({ exhaustedTitle: e.target.value })}
        />
      </label>
      <label className="cfg-field">
        <span>Out-of-questions subtext</span>
        <input
          value={askAi.copy.exhaustedSub}
          maxLength={160}
          onChange={(e) => setAskCopy({ exhaustedSub: e.target.value })}
        />
      </label>
    </div>
  );
}

function SettingsRead({ cfg }: { cfg: AppConfig }) {
  const bug = useBugMode();
  const f = cfg.footer;
  const yearLabel =
    f.copyrightYear === "auto" ? `Auto (${new Date().getFullYear()})` : String(f.copyrightYear);
  const fmt = TITLE_FORMATS.find((t) => t.value === f.titleFormat)?.label ?? f.titleFormat;
  const header =
    [cfg.header.caseStudy && "Case Study", cfg.header.designSystem && "Design System"]
      .filter(Boolean)
      .join(", ") || "None";
  const items =
    FOOTER_ITEMS.filter((it) => f.show[it.key])
      .map((it) => it.label)
      .join(", ") || "None";
  const ask = cfg.askAi ?? BASELINE_CONFIG.askAi;
  const rows: [string, string][] = [
    ["Site mode", cfg.interactive ? "Interactive" : "Lights-out (Print + BYO-AI only)"],
    ["App title", cfg.appTitle],
    ["Header", header],
    ["Footer title", fmt],
    ["Copyright year", yearLabel],
    ["Footer items", items],
    ["Ask AI", `${ask.freeLimit} free · +${ask.emailGrant} per email`],
    ["Bug picker", cfg.feedback?.bugPickerPublic ? "Everyone" : "Admin only"],
  ];
  return (
    <div className="cfg-read">
      {rows.map(([k, v]) => (
        <div className="cfg-read-row" key={k}>
          <span className="cfg-read-k mono">{k}</span>
          <span className="cfg-read-v">{v}</span>
        </div>
      ))}
      <p className="cfg-section-label mono" style={{ marginTop: 10 }}>
        RED Mode
      </p>
      <div className="cfg-toggles">
        <Toggle
          label="RED Mode (this device)"
          checked={bug.on}
          onChange={(v) => bugMode.toggle(v)}
        />
      </div>
      <p className="cfg-soon-note mono">
        Point-and-click work-order generation — Requirements · Enhancements · Defects. Immediate and
        device-local (not part of the published config, no Edit/Save needed). While on, the viewport
        is framed and ⌥-click pins elements; classify each pin, then file them as items or bundle the
        session into one work order in the Tracker.
      </p>
      <p className="cfg-section-label mono" style={{ marginTop: 10 }}>
        Appearance
      </p>
      <AppearanceRead appearance={cfg.appearance} />
    </div>
  );
}
